import { type FilterViewModel } from '@/infra/FiltersViewModel'
import { isNil } from 'lodash-es'
import { action, computed, observable } from 'mobx'
import { formatMoney } from '@/utils/money-util'

/**
 * Number range a {@link NumberRangeFilter} filters by. Either end may be open.
 */
export interface NumberRange {
  min: number | null
  max: number | null
}

/**
 * Filter for a number range, optionally formatted as money.
 */
export class NumberRangeFilter<TKey> implements FilterViewModel<
  TKey,
  NumberRange
> {
  /** @inheritDoc */
  key: TKey

  /** @inheritDoc */
  title: string

  /**
   * Formats the values as money when set.
   */
  currencyCode: string | undefined

  /**
   * Formats the values when there is no currency.
   */
  numberFormatter = new Intl.NumberFormat('en-US')

  /** @inheritDoc */
  @observable accessor value: NumberRange | null = null

  /** @inheritDoc */
  @observable accessor editing = false

  constructor(key: TKey, title: string, currencyCode?: string) {
    this.key = key
    this.title = title
    this.currencyCode = currencyCode
  }

  /** @inheritDoc */
  @computed
  get valid() {
    return !isNil(this.value)
  }

  /** @inheritDoc */
  @computed
  get valueText() {
    if (!this.value?.min && !this.value?.max) {
      return 'No min - No max'
    }
    if (!this.value?.min) {
      return `max ${this.formatValue(this.value?.max ?? 0)}`
    }
    if (!this.value?.max) {
      return `min ${this.formatValue(this.value?.min ?? 0)}`
    }

    return `${this.formatValue(this.value.min)} - ${this.formatValue(this.value.max)}`
  }

  /** @inheritDoc */
  toString() {
    if (!this.value?.min && !this.value?.max) {
      return `${this.title}: No min - No max`
    }

    if (!this.value?.min) {
      return `${this.title} max: ${this.formatValue(this.value?.max ?? 0)}`
    }
    if (!this.value?.max) {
      return `${this.title} min: ${this.formatValue(this.value?.min ?? 0)}`
    }

    return `${this.title}: ${this.formatValue(
      this.value?.min,
    )} - ${this.formatValue(this.value?.max)}`
  }

  /**
   * Sets the lower bound.
   *
   * @param val
   */
  @action.bound
  setMin(val: number | null) {
    this.value = {
      min: val,
      max: this.value?.max ?? null,
    }
  }

  /**
   * Sets the upper bound.
   *
   * @param val
   */
  @action.bound
  setMax(val: number | null) {
    this.value = {
      min: this.value?.min ?? null,
      max: val,
    }
  }

  /** @inheritDoc */
  @action.bound
  clearValue() {
    this.value = null
  }

  /** @inheritDoc */
  @action.bound
  startEditing() {
    this.editing = true
  }

  /** @inheritDoc */
  @action.bound
  stopEditing() {
    this.editing = false
  }

  /**
   * Clears the value and closes the editor.
   */
  @action.bound
  clear() {
    this.clearValue()
    this.stopEditing()
  }

  /**
   * Formats a bound for display.
   *
   * @param value
   */
  formatValue(value: number) {
    // We only support formatting as USD, but can change this if ever needed.
    return this.currencyCode
      ? formatMoney(value)
      : this.numberFormatter.format(value)
  }

  /**
   * Ranges are not persisted.
   */
  serialize() {
    return null
  }

  /**
   * Restores the range from storage.
   */
  deserialize() {
    // Ranges are not persisted, so there is nothing to restore.
  }
}
